import { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import {
    SelectList,
    Button,
    LoadingSpinner,
    EMPTY_IMAGE,
    Back,
    Typography,
    ColContainer,
    RowContainer,
    toast,
    Input,
} from '../../components/common';
import { useGetUser } from '../../contexts/user';
import ConnectCCP from './helpers/connectccp';
import { GetInstanceUser, UpdateInstanceUser } from '../../services/Api/users/useApiUser';
import useInterval from '../../services/Helpers/useInterval';
import TPSLookup from '../../components/TPSLookup';
import { GetQueueStatuses } from '../../services/Api/queues/useApiQueues';
import { UpdateHubContext } from '../../contexts/hub/updates';
import { OverrideTPS } from '../../services/Api/tpsLookUp/useApiTpsLookUp';
import {
    PauseRecording,
    ResumeRecording,
    StopRecording,
} from '../../services/Api/recordings/useApiRecordings';
import RecordingIcon from '../../components/Recording';
import { GetRoutingProfiles } from '../../services/Api/routingProfiles/useApiRoutingProfiles';

const Instance = () => {
    const { id } = useParams();
    const ctx = useContext(UpdateHubContext);
    const [routingProfiles, setRoutingProfiles] = useState([]);
    const [isLoadingProfiles, setIsLoadingProfiles] = useState(true);

    useEffect(() => { 
        GetRoutingProfiles({ instanceId: id }).then((res) => {
            setRoutingProfiles(res.data.RoutingProfileSummaryList?.map((r) => ({
                value: r.Id,
                label: r.Name,
            })));
        })
        setIsLoadingProfiles(false);
    }, [id]);

    if (isLoadingProfiles) return <LoadingSpinner />;
    return <InstanceLoaded ctx={ctx} routingProfiles={routingProfiles} />;
}

const InstanceLoaded = ({ctx, routingProfiles}) => {
    
    const [isLoading, setIsLoading] = useState(true);
    
    const [autoAcceptToggling, setAutoAcceptToggling] = useState(false);
    const [routingProfileChanging, setRoutingProfileChanging] = useState(false);
    const [call, setCall] = useState(null);
    const [pauseStopRecordingReason, setPauseStopRecordingReason] = useState('');
    const [recordingState, setRecordingState] = useState('STOPPED');

    // Instance id
    const { id } = useParams();

    const updateRoutingProfile = (routingProfile) => {
        setRoutingProfileChanging(true);
        UpdateInstanceUser(
            { instanceId: id },
            {
                UserId: instance.user.Id,
                PhoneConfig: instance.user.PhoneConfig,
                RoutingProfileId: routingProfile,
                SecurityProfileIds: instance.user.SecurityProfileIds,
                IdentityInfo: { ...instance.user.IdentityInfo, Email: userDetails.username },
            }
        )
            .then((response) => {
                if (response.data) {
                    setCurrentRoutingProfile(
                        routingProfiles?.find((rp) => rp.value === routingProfile)
                    );
                }
                setRoutingProfileChanging(false);
            })
            .catch((e) => {
                toast.error();
                console.error(e);
                setRoutingProfileChanging(false);
            });
    };

    const updateAutoAccept = () => {
        setAutoAcceptToggling(true);
        const newAutoAccept = !autoAccept;
        UpdateInstanceUser(
            { instanceId: id },
            {
                UserId: instance.user.Id,
                PhoneConfig: {
                    ...instance.user.PhoneConfig,
                    AutoAccept: newAutoAccept,
                },
                RoutingProfileId: currentRoutingProfile.value,
                SecurityProfileIds: instance.user.SecurityProfileIds,
                IdentityInfo: { ...instance.user.IdentityInfo, Email: userDetails.username },
            }
        )
            .then((response) => {
                if (response.data) {
                    setAutoAccept(newAutoAccept);
                    setAutoAcceptToggling(false);
                }
            })
            .catch((e) => {
                toast.error();
                console.error(e);
                setAutoAcceptToggling(false);
            });
    };

    const [instanceUser, setInstanceUser] = useState({});
    const [contactsInQueue, setContactsInQueue] = useState(0);

    const [autoAccept, setAutoAccept] = useState(false);
    const [currentRoutingProfile, setCurrentRoutingProfile] = useState();

    const { userDetails, hasPermission, instances } = useGetUser();
    const instance = (instances() || []).filter((i) => i.user).find((i) => i.instanceid === id);

    const tpsEnabled = !!instance?.tps || false;

    // Permissions
    const canOverrideProfile = hasPermission('irp');
    const canPause = hasPermission('ipsr');
    const canToggleAutoAccept = hasPermission('iaa');

    useEffect(() => {
        ctx.webSocket.connection.on('schedule-updates', (x) => {
            const entryForUser = x.find(
                (bigX) => bigX.email === userDetails.username && bigX.instanceId === id
            );
            if (entryForUser) {
                const newRoutingProfile = routingProfiles?.find(
                    (rp) => rp.value === entryForUser.routingProfileId
                );
                setCurrentRoutingProfile(newRoutingProfile);
                setAutoAccept(entryForUser.autoAccept);
            }
        });
        return () => {
            ctx.webSocket.connection.off('schedule-updates');
        };
        //eslint-disable-next-line
    }, [ctx]);

    useEffect(() => {
        if (id && instance?.user?.Id) {
            GetInstanceUser({ instanceId: id, userId: instance.user.Id }).then((res) => {
                setInstanceUser(res.data.User);
            });
            setIsLoading(false);    
        }
    }, [id, instance]);

    useEffect(() => {
        if (instanceUser && instance) {
            setAutoAccept(instanceUser?.PhoneConfig?.AutoAccept);
            setCurrentRoutingProfile(
                routingProfiles?.find((r) => r.value === instanceUser?.RoutingProfileId)
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [instanceUser, instance]);

    useInterval(() => {
        GetQueueStatuses({ instanceId: id }).then((stats) => {
            let newContactsInQueue = 0;
            const metricResults = stats?.data?.MetricResults;
            if (metricResults && metricResults.length) {
                metricResults.forEach((metricResult) => {
                    const metricResultsCollections = metricResult.Collections;
                    if (metricResultsCollections) {
                        metricResultsCollections.forEach((item) => {
                            switch (item.Metric?.Name) {
                                case 'CONTACTS_IN_QUEUE':
                                    newContactsInQueue += item.Value;
                                    break;
                                default:
                                    break;
                            }
                        });
                    }
                });
            }
            setContactsInQueue(newContactsInQueue);
        });
    }, 10000);

    const submitOverride = (data) => {
        OverrideTPS({ instanceId: id }, { ...data, User: userDetails.username });
    };

    const pauseResumeRecording = () => {
        if (recordingState === 'RECORDING') {
            PauseRecording(
                id,
                pauseStopRecordingReason,
                call.contactId,
                call.initialContactId,
                userDetails.username
            ).then(() => {
                setPauseStopRecordingReason('');
                setRecordingState('PAUSED');
            });
        } else {
            ResumeRecording(id, call.contactId, call.initialContactId, userDetails.username).then(
                () => {
                    setRecordingState('RECORDING');
                }
            );
        }
    };

    const stopRecording = () => {
        if (recordingState === 'RECORDING') {
            StopRecording(
                id,
                pauseStopRecordingReason,
                call.contactId,
                call.initialContactId,
                userDetails.username
            ).then(() => {
                setRecordingState('STOPPED');
            });
        }
    };

    return isLoading ? (
        <LoadingSpinner />
    ) : (
        <>
            <Back>Back to Dashboard</Back>
            <div className="grid grid-cols-2 gap-8" style={{ minHeight: '650px' }}>
                {/* Keypad Column */}
                <div className="keypad h-full">
                    <ConnectCCP
                        instance={instance}
                        setCall={setCall}
                        setRecordingState={setRecordingState}
                        setPauseStopRecordingReason={setPauseStopRecordingReason}
                    />
                </div>
                {/* Instance info column */}
                <ColContainer spacing="large">
                    <ColContainer spacing="large" padding="x-large" className="bg-white rounded-lg">
                        <RowContainer spacing="large" className="flex items-center">
                            <img
                                className="max-h-20 max-w-20"
                                src={`data:image/png;base64,${
                                    instance?.logo || EMPTY_IMAGE.base64
                                }`}
                                alt={instance?.company}
                            />
                            <ColContainer>
                                <Typography variant="heading1.1">{instance?.company}</Typography>
                                <Typography variant="heading2.3">{instance?.project}</Typography>
                            </ColContainer>
                        </RowContainer>
                        <ColContainer>
                            <Typography variant="body3" color="grey-normal" className="pb-1">
                                Routing Profile
                            </Typography>
                            {routingProfileChanging || !currentRoutingProfile ? (
                                <LoadingSpinner />
                            ) : (
                                <SelectList
                                    disabled={!canOverrideProfile}
                                    onChange={({ value }) => updateRoutingProfile(value)}
                                    options={routingProfiles}
                                    value={currentRoutingProfile}
                                />
                            )}
                        </ColContainer>
                        <div className="grid grid-cols-2 gap-4">
                            <RowContainer className="items-center">
                                <Typography variant="heading4" color="grey-dark" className="w-28">
                                    Auto Accept
                                </Typography>
                                {autoAcceptToggling ? (
                                    <LoadingSpinner />
                                ) : (
                                    <Button
                                        disabled={!canToggleAutoAccept}
                                        onClick={() => updateAutoAccept()}
                                        type="toggle"
                                    >
                                        {autoAccept ? 'On' : 'Off'}
                                    </Button>
                                )}
                            </RowContainer>
                            <ColContainer className="self-center" spacing="small">
                                <RowContainer>
                                    <Typography
                                        variant="heading4"
                                        color="grey-dark"
                                        className="w-36"
                                    >
                                        Calls In Queue
                                    </Typography>
                                    <Typography variant="heading3.2">{contactsInQueue}</Typography>
                                </RowContainer>
                            </ColContainer>
                        </div>
                        {canPause && (
                            <ColContainer>
                                <Typography variant="body3" color="grey-normal" className="pb-1">
                                    Reason for Pause/Stop Recording
                                </Typography>
                                <RowContainer className="items-center" spacing="small">
                                    <RecordingIcon state={recordingState} />
                                    <div style={{ flexGrow: '4' }}>
                                        <Input
                                            disabled={!call || recordingState !== 'RECORDING'}
                                            value={pauseStopRecordingReason}
                                            onChange={(e) =>
                                                setPauseStopRecordingReason(e.target?.value || '')
                                            }
                                        />
                                    </div>
                                    <Button
                                        type="secondary"
                                        disabled={
                                            !call ||
                                            (recordingState === 'RECORDING' &&
                                                !pauseStopRecordingReason) ||
                                            recordingState === 'STOPPED'
                                        }
                                        onClick={pauseResumeRecording}
                                    >
                                        {recordingState === 'PAUSED' ? 'Resume' : 'Pause'}
                                    </Button>
                                    <Button
                                        type="secondary"
                                        disabled={
                                            !call ||
                                            !pauseStopRecordingReason ||
                                            recordingState === 'STOPPED'
                                        }
                                        onClick={stopRecording}
                                    >
                                        Stop
                                    </Button>
                                </RowContainer>
                            </ColContainer>
                        )}
                    </ColContainer>
                    {tpsEnabled ? (
                        <ColContainer
                            padding="x-large"
                            spacing="large"
                            className="rounded-lg bg-white"
                        >
                            <Typography variant="heading1.1">TPS Lookup</Typography>
                            <TPSLookup submitOverride={submitOverride} />
                        </ColContainer>
                    ) : (
                        <></>
                    )}
                </ColContainer>
            </div>
        </>
    );
};

export default Instance;
